<template>
  <aside
    class="sidebar"
    :class="{
      'sidebar--collapsed': collapsed,
      'sidebar--mobile-open': mobileOpen
    }"
  >
    <!-- Brand -->
    <div class="sidebar__brand">
      <Logo class="sidebar__logo" />

      <button
        v-if="collapsible"
        type="button"
        class="sidebar__collapse-button"
        :aria-label="collapsed ? 'Perluas sidebar' : 'Ciutkan sidebar'"
        @click="$emit('toggle-collapse')"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" width="20" height="20">
          <path :d="icons.chevronLeft" fill="currentColor" />
        </svg>
      </button>

      <!-- Mobile close button -->
      <button
        type="button"
        class="sidebar__mobile-close"
        aria-label="Tutup sidebar"
        @click="$emit('close-mobile')"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" width="20" height="20">
          <path :d="icons.close" fill="currentColor" />
        </svg>
      </button>
    </div>

    <!-- Navigation -->
    <nav class="sidebar__navigation" aria-label="Navigasi utama">
      <template
        v-for="(section, index) in normalizedSections"
        :key="sectionKey(section, index)"
      >
        <SidebarSection
          :title="section.title"
          :items="section.items"
          :collapsed="collapsed"
        />
      </template>
    </nav>

    <div class="sidebar__footer">
      <slot name="footer" />
    </div>
  </aside>
</template>

<script>
import { mdiChevronLeft, mdiClose } from '@mdi/js'
import Logo from '@/components/common/Logo.vue'
import SidebarSection from './SidebarSection.vue'

export default {
  name: 'SidebarComponents',

  components: {
    Logo,
    SidebarSection
  },

  props: {
    menu: {
      type: Array,
      default: () => []
    },

    collapsed: {
      type: Boolean,
      default: false
    },

    collapsible: {
      type: Boolean,
      default: true
    },

    mobileOpen: {
      type: Boolean,
      default: false
    }
  },

  emits: ['toggle-collapse', 'close-mobile'],

  data() {
    return {
      icons: {
        chevronLeft: mdiChevronLeft,
        close: mdiClose
      }
    }
  },

  computed: {
    normalizedSections() {
      const sections = []
      for (let i = 0; i < this.menu.length; i++) {
        const entry = this.menu[i]

        // Format: { title, items }
        if (
          typeof entry === 'object' &&
          entry !== null &&
          !Array.isArray(entry) &&
          entry.title
        ) {
          sections.push({
            title: entry.title,
            items: entry.items || []
          })
          continue
        }

        // Format: string title followed by array of items
        if (typeof entry === 'string') {
          const next = this.menu[i + 1]
          sections.push({
            title: entry,
            items: Array.isArray(next) ? next : []
          })
          if (Array.isArray(next)) i++
          continue
        }

        // Format: array langsung (tanpa title)
        if (Array.isArray(entry)) {
          sections.push({
            title: '',
            items: entry
          })
        }
      }
      return sections
    }
  },

  methods: {
    sectionKey(section, index) {
      return section.title || `section-${index}`
    }
  }
}
</script>

<style scoped lang="scss">

.sidebar {
  position: sticky;
  top: 0;
  align-self: flex-start;

  display: flex;
  flex-direction: column;
  width: 260px;
  height: 100vh;
  background-color: var(--color-background);
  color: var(--color-text);
  border-right: 1px solid var(--color-border);
  overflow: hidden;
  flex-shrink: 0;
  z-index: 20;

  &--collapsed {
    width: 76px;

    .sidebar__logo {
      opacity: 0;
      width: 0;
      overflow: hidden;
    }

    .sidebar__collapse-button svg {
      transform: rotate(180deg);
    }
  }

  &__brand {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 64px;
    padding: 0 16px;
    border-bottom: 1px solid var(--color-border);
    flex-shrink: 0;
    gap: 8px;
  }

  &__logo {
    transition: opacity 0.2s ease, width 0.2s ease;
    flex-shrink: 0;
  }

  &__collapse-button,
  &__mobile-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    color: var(--color-text);
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
    flex-shrink: 0;

    &:hover {
      background: var(--color-surface-hover);
      color: var(--color-text);
    }

    &:focus-visible {
      outline: 2px solid var(--color-button);
      outline-offset: 2px;
    }

    svg {
      transition: transform 0.25s ease;
    }
  }

  &__mobile-close {
    display: none;
  }

  &__navigation {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 16px 12px;
    display: flex;
    flex-direction: column;
    gap: 20px;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--color-border);
      border-radius: 3px;
    }
  }

  &__footer {
    flex-shrink: 0;
    padding: 12px;
  }
}

// Desktop collapsed adjustments
.sidebar--collapsed {
  .sidebar__brand {
    justify-content: center;
    padding: 0 12px;
  }

  .sidebar__navigation {
    padding: 16px 8px;
  }
}

// Mobile drawer
@media (max-width: 768px) {
  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 100;
    width: min(280px, 85vw);
    transform: translateX(-100%);
    box-shadow: 4px 0 24px rgba(15, 23, 42, 0.12);
    transition:
      width 0.25s ease,
      transform 0.25s ease,
      background-color 0.2s ease;

    &--mobile-open {
      transform: translateX(0);
    }

    &--collapsed {
      width: 64px;

      .sidebar__logo {
        opacity: 0;
        width: 0;
        overflow: hidden;
      }

      .sidebar__collapse-button svg {
        transform: rotate(180deg);
      }

      .sidebar__brand {
        justify-content: center;
        padding: 0 8px;
      }

      .sidebar__navigation {
        padding: 16px 6px;
      }
    }
  }

  .sidebar__collapse-button {
    display: none;
  }

  .sidebar__mobile-close {
    display: flex;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sidebar {
    transition: none !important;
  }
}

</style>